import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import { IconButton, TextField, Tooltip } from "@mui/material";
import { DateTimePicker, LocalizationProvider } from "@mui/x-date-pickers";
import { AdapterMoment } from "@mui/x-date-pickers/AdapterMoment";
import moment from "moment";
import { Button, Dropdown, Icon, Toggle } from "pepsico-ds";
import { useCallback, useEffect, useMemo, useState } from "react";
import { useTierRules } from "../../../../hooks/useTierRules";
import { useTiers } from "../../../../hooks/useTiers";
import DeleteConfirmationModal from "../../../common/DeleteConfirmationModal/DeleteConfirmationModal";
import Table from "../../../common/table/Table";
import BoosterStatusTag from "./BoosterStatusTag";
import {
  createEmptyTierMultiplier,
  filterTierMultipliers,
  getTierMultiplierValidationErrors,
} from "./pointsOutcomeUtils";
import TableFilterBar from "./TableFilterBar";
import { TIME_BOUND_FILTER_FIELDS } from "./tableFilterConfig";

const DEFAULT_FILTERS = { tierId: "" };
const TIME_BOUND_FILTERS = {
  tierId: "",
  startDate: "",
  endDate: "",
  status: "",
};

/**
 * TierMultiplierConfig — used in award-purchase-based-points.
 * Provides Default and Time Bounded tabs.
 * Default tab: read-only display of tiers from the tier management system.
 * Time Bounded tab: user-configurable overrides that temporarily replace defaults.
 */
const TierMultiplierConfig = ({
  enabled = false,
  onToggle,
  timeBoundMultipliers = [],
  onChangeTimeBound,
  onValidationChange,
}) => {
  const { tierRules } = useTierRules();
  const { tiers, loading: tiersLoading } = useTiers();
  const [activeTab, setActiveTab] = useState("default");
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [defaultFilters, setDefaultFilters] = useState(DEFAULT_FILTERS);
  const [timeBoundFilters, setTimeBoundFilters] = useState(TIME_BOUND_FILTERS);
  const [touchedRows, setTouchedRows] = useState({});

  useEffect(() => {
    if (!enabled) {
      setActiveTab("default");
    }
  }, [enabled]);

  // Compute validation errors for all time-bound multipliers (only for touched rows)
  const errors = useMemo(() => {
    return timeBoundMultipliers.reduce((acc, multiplier) => {
      // Only validate rows that have been touched
      if (!touchedRows[multiplier.id]) {
        return acc;
      }
      const multiplierErrors =
        getTierMultiplierValidationErrors(multiplier, timeBoundMultipliers) || {};
      if (Object.keys(multiplierErrors).length > 0) {
        acc[multiplier.id] = multiplierErrors;
      }
      return acc;
    }, {});
  }, [timeBoundMultipliers, touchedRows]);

  const hasValidationErrors = Object.keys(errors).length > 0;

  // Propagate validation state to parent
  useEffect(() => {
    onValidationChange?.(hasValidationErrors);
  }, [hasValidationErrors, onValidationChange]);

  const isTierActive = tierRules?.status === "ACTIVE";

  // Object-based options for pepsico-ds Dropdown
  const tierOptions = useMemo(
    () => tiers.map((t) => ({ id: t.id, displayText: t.name })),
    [tiers]
  );

  const defaultFilterFields = useMemo(
    () => [
      {
        key: "tierId",
        label: "Tier",
        type: "select",
        emptyOptionLabel: "All tiers",
        options: tierOptions.map((tier) => ({
          value: tier.id,
          label: tier.displayText,
        })),
      },
    ],
    [tierOptions]
  );

  const timeBoundFilterFields = useMemo(
    () => [...defaultFilterFields, ...TIME_BOUND_FILTER_FIELDS],
    [defaultFilterFields]
  );

  // Time-bound tab handlers
  const handleAddTimeBound = useCallback(() => {
    const newEntry = createEmptyTierMultiplier();
    onChangeTimeBound([...timeBoundMultipliers, newEntry]);
  }, [timeBoundMultipliers, onChangeTimeBound]);

  const handleUpdateTimeBound = useCallback(
    (id, field, value) => {
      // Mark the row as touched when user modifies any field
      setTouchedRows((prev) => ({ ...prev, [id]: true }));
      const updated = timeBoundMultipliers.map((r) =>
        r.id === id ? { ...r, [field]: value } : r
      );
      onChangeTimeBound(updated);
    },
    [timeBoundMultipliers, onChangeTimeBound]
  );

  const handleDeleteTimeBound = useCallback(
    (id) => {
      onChangeTimeBound(timeBoundMultipliers.filter((r) => r.id !== id));
      setDeleteTarget(null);
    },
    [timeBoundMultipliers, onChangeTimeBound]
  );

  // Default tab columns — read-only, sourced from useTiers
  const defaultHeaderData = useMemo(
    () => [
      { columnKey: "tier_name", header: "TIER NAME" },
      { columnKey: "multiplier", header: "MULTIPLIER" },
    ],
    []
  );

  const filteredDefaultTiers = useMemo(
    () => filterTierMultipliers(tiers, defaultFilters),
    [tiers, defaultFilters]
  );

  const defaultRowData = useMemo(
    () =>
      filteredDefaultTiers.map((t) => ({
        id: t.id,
        tier_name: t.name,
        multiplier: t.multiplier != null ? `${t.multiplier}x` : "-",
      })),
    [filteredDefaultTiers]
  );

  // Time-bound table columns
  const timeBoundHeaderData = useMemo(
    () => [
      {
        columnKey: "tier_name",
        header: "TIER NAME",
        render: (row) => {
          const rawIds = row._rawTierIds || [];
          const selectedValue = tierOptions.filter((t) => rawIds.includes(t.id));
          const placeholder =
            selectedValue.length > 0
              ? `${selectedValue[0].displayText} ${selectedValue.length > 1 ? "(" + (selectedValue.length - 1) + " more)" : ""}`
              : "Select tiers";
          return (
            <Dropdown
              childList={tierOptions}
              label="Tiers"
              selection="multiple"
              selectedValue={selectedValue.length ? selectedValue : undefined}
              placeholder={placeholder}
              size="small"
              setSelectedValue={(val) => {
                const ids = (val || []).map((v) => v.id);
                handleUpdateTimeBound(row.id, "tier_id", ids);
              }}
            />
          );
        },
      },
      {
        columnKey: "value",
        header: "BOUNDED VALUE",
        render: (row) => (
          <TextField
            size="small"
            type="number"
            placeholder="e.g. 3x"
            value={row._rawValue ?? ""}
            error={!!errors[row.id]?.value}
            onChange={(e) => {
              const val = e.target.value;
              if (val === "" || Number(val) >= 0) {
                handleUpdateTimeBound(row.id, "value", val);
              }
            }}
            inputProps={{ min: 0 }}
            InputProps={{
              endAdornment: (
                <span style={{ fontSize: "0.8rem", color: "#666" }}>x</span>
              ),
            }}
            style={{ width: 120 }}
          />
        ),
      },
      {
        columnKey: "start_date",
        header: "START DATE",
        render: (row) => {
          const now = moment();
          return (
            <LocalizationProvider dateAdapter={AdapterMoment}>
              <DateTimePicker
                value={
                  row.effective_start_date ? moment(row.effective_start_date) : null
                }
                minDateTime={now}
                onAccept={(val) => {
                  handleUpdateTimeBound(
                    row.id,
                    "effective_start_date",
                    val ? val.toISOString() : ""
                  );
                }}
                timeSteps={{ minutes: 1 }}
                closeOnSelect={false}
                slotProps={{
                  textField: {
                    size: "small",
                    error:
                      !!errors[row.id]?.startDate || !!errors[row.id]?.dateRange,
                  },
                  actionBar: {
                    actions: ["clear", "accept"],
                  },
                }}
              />
            </LocalizationProvider>
          );
        },
      },
      {
        columnKey: "end_date",
        header: "END DATE",
        render: (row) => {
          const now = moment();
          const minDateTime = row.effective_start_date
            ? moment(row.effective_start_date)
            : now;
          return (
            <LocalizationProvider dateAdapter={AdapterMoment}>
              <DateTimePicker
                value={
                  row.effective_end_date ? moment(row.effective_end_date) : null
                }
                minDateTime={minDateTime.isAfter(now) ? minDateTime : now}
                onAccept={(val) => {
                  handleUpdateTimeBound(
                    row.id,
                    "effective_end_date",
                    val ? val.toISOString() : ""
                  );
                }}
                timeSteps={{ minutes: 1 }}
                closeOnSelect={false}
                slotProps={{
                  textField: {
                    size: "small",
                    style: { width: 200 },
                    error: !!errors[row.id]?.endDate || !!errors[row.id]?.dateRange,
                  },
                  actionBar: {
                    actions: ["clear", "accept"],
                  },
                }}
              />
            </LocalizationProvider>
          );
        },
      },
      {
        columnKey: "status",
        header: "STATUS",
        render: (row) => (
          <BoosterStatusTag
            startDate={row.effective_start_date}
            endDate={row.effective_end_date}
          />
        ),
      },
      {
        columnKey: "actions",
        header: "",
        render: (row) => (
          <Tooltip title="Delete">
            <IconButton size="small" onClick={() => setDeleteTarget({ id: row.id })}>
              <DeleteOutlineIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        ),
      },
    ],
    [tierOptions, handleUpdateTimeBound, errors]
  );

  const filteredTimeBoundMultipliers = useMemo(
    () => filterTierMultipliers(timeBoundMultipliers, timeBoundFilters),
    [timeBoundMultipliers, timeBoundFilters]
  );

  const timeBoundRowData = useMemo(
    () =>
      filteredTimeBoundMultipliers.map((r) => ({
        ...r,
        _rawTierIds: r.tier_id,
        _rawValue: r.value,
        tier_name: Array.isArray(r.tier_id)
          ? r.tier_id
              .map((id) => tierOptions.find((t) => t.id === id)?.displayText)
              .filter(Boolean)
              .join(", ")
          : "",
        value: r.value ? `x${r.value}` : "",
      })),
    [filteredTimeBoundMultipliers, tierOptions]
  );

  const hasDefaultFilters = Object.values(defaultFilters).some(Boolean);
  const hasTimeBoundFilters = Object.values(timeBoundFilters).some(Boolean);

  return (
    <div className="section-card tier-multiplier-section">
      <div className="section-header">
        <h3>Tier multipliers</h3>
        <div className="enable-toggle">
          <span>Enable</span>
          <Toggle checked={enabled} onUpdate={(val) => onToggle(val)} size="small" />
        </div>
      </div>

      {!enabled && (
        <div className="tier-inactive-message">
          <Icon icon="info" size="small" />
          <div>
            <strong>
              Tier multipliers will not be applied to point calculations
            </strong>
            <div>
              Any tier-based multiplier, whether default or time-bound, will be
              ignored.
            </div>
          </div>
        </div>
      )}

      {enabled && !isTierActive && !tiersLoading && (
        <div className="tier-inactive-message">
          <Icon icon="info" size="small" />
          <div>
            <strong>Tier multipliers are unavailable.</strong>
            <div>
              No active tier structure is configured for this program. Create and
              activate a tier structure to enable tier multipliers.
            </div>
          </div>
        </div>
      )}

      <div className="config-tabs">
        <button
          className={`config-tab ${activeTab === "default" ? "active" : ""}`}
          onClick={() => setActiveTab("default")}
        >
          Default
        </button>
        {enabled && (
          <button
            className={`config-tab ${activeTab === "timeBound" ? "active" : ""}`}
            onClick={() => setActiveTab("timeBound")}
          >
            Time Bounded
            <Icon
              icon="info"
              size="small"
              title="During the validity period, the Time Bounded rule will override the default rule."
            />
          </button>
        )}
      </div>

      {activeTab === "default" && !tiersLoading && (
        <>
          <TableFilterBar
            filters={defaultFilters}
            fields={defaultFilterFields}
            onChange={(key, value) =>
              setDefaultFilters((current) => ({ ...current, [key]: value }))
            }
            onClear={() => setDefaultFilters(DEFAULT_FILTERS)}
          />
          <Table
            headerData={defaultHeaderData}
            rowData={defaultRowData}
            pagination
            rowsPerPage={10}
            noDataLabel={
              hasDefaultFilters
                ? "No tiers match the filters"
                : "No tiers configured. Add tiers in Tier Management."
            }
          />
        </>
      )}

      {enabled && activeTab === "timeBound" && (
        <>
          <TableFilterBar
            filters={timeBoundFilters}
            fields={timeBoundFilterFields}
            onChange={(key, value) =>
              setTimeBoundFilters((current) => ({ ...current, [key]: value }))
            }
            onClear={() => setTimeBoundFilters(TIME_BOUND_FILTERS)}
          />
          <div className="add-multiplier-row">
            <Button size="small" variant="primary" onClick={handleAddTimeBound}>
              Add Multiplier
            </Button>
          </div>
          <Table
            headerData={timeBoundHeaderData}
            rowData={timeBoundRowData}
            pagination
            rowsPerPage={10}
            noDataLabel={
              hasTimeBoundFilters
                ? "No time-bounded tier multipliers match the filters"
                : "No time-bounded tier multipliers configured"
            }
          />
          {hasValidationErrors && (
            <div className="validation-error">
              {[
                ...new Set(
                  Object.values(errors).flatMap((entryErrors) =>
                    Object.values(entryErrors)
                  )
                ),
              ].map((msg, idx) => (
                <div key={idx}>
                  <span>&#9888;</span> {msg}
                </div>
              ))}
            </div>
          )}
        </>
      )}

      {tiersLoading && <p className="section-subtitle">Loading tiers...</p>}

      <DeleteConfirmationModal
        isOpen={Boolean(deleteTarget)}
        title="Delete Time-Bounded Rule?"
        message="This action cannot be undone. If the rule is currently active, it will stop being applied immediately."
        onConfirm={() => handleDeleteTimeBound(deleteTarget?.id)}
        onCancel={() => setDeleteTarget(null)}
      />
    </div>
  );
};

export default TierMultiplierConfig;
